// src/components/file/FilterRail.jsx
import React, { useCallback, useEffect, useRef } from 'react';
import '../../css/FilterRail.css';

export const RAIL_MIN = 220;
export const RAIL_MAX = 520;
export const RAIL_DEFAULT = 288;
/** One arrow press. Big enough to feel, small enough to land on a value you meant. */
export const RAIL_STEP = 16;

export const clampRailWidth = (width) =>
    Math.min(RAIL_MAX, Math.max(RAIL_MIN, Math.round(Number(width) || RAIL_DEFAULT)));

export const isRailWidth = (width) => Number.isFinite(width) && width >= RAIL_MIN && width <= RAIL_MAX;

/**
 * The width a drag has reached. Pulled out as a pure function because jsdom
 * ships no PointerEvent — a synthesised pointermove arrives as a bare Event
 * with no coordinates — so the arithmetic is tested here rather than through a
 * drag that cannot carry a clientX.
 */
export const widthFromDrag = ({ startX, startWidth }, clientX) => clampRailWidth(startWidth + (clientX - startX));

/**
 * The filter panel's column: how wide it is, and whether it's there at all.
 *
 * The rail was a fixed 288px that could not be moved or dismissed. On a 1280px
 * laptop that is a fifth of the window spent on filters you may not be using —
 * and facet values long enough to be truncated had nowhere to go.
 *
 * The drag handle is a real `separator` widget: arrow keys resize it too, so
 * the layout isn't only adjustable with a mouse.
 */
export default function FilterRail({ width, collapsed, onWidthChange, children }) {
    const dragRef = useRef(null);

    const onPointerMove = useCallback((e) => {
        const drag = dragRef.current;
        if (!drag || !Number.isFinite(e.clientX)) return;
        onWidthChange(widthFromDrag(drag, e.clientX));
    }, [onWidthChange]);

    const endDrag = useCallback(() => {
        dragRef.current = null;
        document.body.classList.remove('is-resizing-col');
    }, []);

    useEffect(() => {
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', endDrag);
        window.addEventListener('pointercancel', endDrag);
        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', endDrag);
            window.removeEventListener('pointercancel', endDrag);
            document.body.classList.remove('is-resizing-col');
        };
    }, [onPointerMove, endDrag]);

    if (collapsed) return null;

    const onPointerDown = (e) => {
        dragRef.current = { startX: e.clientX, startWidth: width };
        // Stops the drag from selecting the facet labels it passes over.
        document.body.classList.add('is-resizing-col');
        e.preventDefault();
    };

    const onKeyDown = (e) => {
        const by = { ArrowLeft: -RAIL_STEP, ArrowRight: RAIL_STEP };
        if (e.key in by) {
            e.preventDefault();
            onWidthChange(clampRailWidth(width + by[e.key]));
        } else if (e.key === 'Home') {
            e.preventDefault();
            onWidthChange(RAIL_MIN);
        } else if (e.key === 'End') {
            e.preventDefault();
            onWidthChange(RAIL_MAX);
        }
    };

    return (
        <div className="fb-rail" id="qd-filter-rail" style={{ width: `${width}px` }}>
            {children}
            <div
                className="fb-rail-handle"
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize filters"
                aria-valuenow={width}
                aria-valuemin={RAIL_MIN}
                aria-valuemax={RAIL_MAX}
                tabIndex={0}
                title="Drag, or use the arrow keys, to resize"
                onPointerDown={onPointerDown}
                onKeyDown={onKeyDown}
                onDoubleClick={() => onWidthChange(RAIL_DEFAULT)}
            />
        </div>
    );
}
